/**
 * ActivityPage — `/activity`.
 *
 * Unified timeline of every engine event over the last 14 days:
 *   • irrigation fires (✓ green)
 *   • irrigation skips (— amber, with the reason)
 *   • fire verifications (⚡ emerald when soil moisture rose)
 *   • PGR applications (🌿 violet)
 *
 * Powered by GET /api/dashboard/activity which merges
 * irrigation_schedule_runs + fire_verifications + pgr_applications into a
 * single sorted list.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Droplets, SkipForward, CheckCircle2, Leaf, Home, Clock, BarChart3, Bell, Settings as SettingsIcon, Trophy, Thermometer, Wand2, Sparkles } from 'lucide-react';
import { useUnreadAlerts } from '../../hooks/useUnreadAlerts';
import { AlertDot } from '../../components/pulse/AlertDot';
import { API_URL } from '../../config/api';

const STYLES = {
  irrigation_fire:   { icon: Droplets,      color: 'text-sky-400',     ring: 'border-sky-900/60'     },
  irrigation_skip:   { icon: SkipForward,   color: 'text-amber-400',   ring: 'border-amber-900/60'   },
  fire_verification: { icon: CheckCircle2,  color: 'text-emerald-400', ring: 'border-emerald-900/60' },
  pgr_application:   { icon: Leaf,          color: 'text-purple-400',  ring: 'border-purple-900/60'  },
};

function relTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const m = Math.round((Date.now() - d.getTime()) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const dd = Math.round(h / 24);
    return `${dd}d ago`;
  } catch { return ''; }
}

function dayHeader(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined,
      { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return ''; }
}

export default function ActivityPage() {
  const navigate = useNavigate();
  const { total: unreadTotal, high: unreadHigh } = useUnreadAlerts();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [stats, setStats] = useState(null);
  const [applyHistory, setApplyHistory] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/dashboard/activity?days=14`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(setData)
      .catch(e => setErr(e.message));
    fetch(`${API_URL}/api/moisture-alerts/stats?days=30`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(setStats)
      .catch(() => {});
    fetch(`${API_URL}/api/dashboard/apply-history?days=90`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(setApplyHistory)
      .catch(() => {});
  }, []);

  // Group events by day for the date headers
  const grouped = (() => {
    if (!data?.events) return [];
    const out = []; let lastDay = null; let bucket = null;
    for (const ev of data.events) {
      const day = (ev.ts || '').slice(0, 10);
      if (day !== lastDay) {
        bucket = { day, label: dayHeader(ev.ts), items: [] };
        out.push(bucket); lastDay = day;
      }
      bucket.items.push(ev);
    }
    return out;
  })();

  return (
    <div className="min-h-screen bg-black text-slate-100 pb-24">
      <div className="max-w-md mx-auto px-4 pt-3">
        <div className="flex items-center justify-between py-2">
          <button data-testid="activity-back"
                  onClick={() => navigate('/pulse')}
                  className="p-2 -ml-2 rounded-full hover:bg-slate-800">
            <ChevronLeft className="w-6 h-6 text-slate-200" />
          </button>
          <div className="text-base font-semibold tracking-tight">Activity · 14 day</div>
          <span className="w-9" />
        </div>

        <div className="text-xs text-slate-400 px-1 mb-3">
          Every fire, skip, and application the engine made — receipts you can show off.
        </div>

        {/* Leaderboard pill — TurfPulse's real-world receipt.
            "Alerted to X dry-outs · saved Y times" is the FB-shareable
            proof point Inner Circle members will screenshot. */}
        {stats && (stats.alerted > 0 || stats.dollars_saved > 0 || stats.gate_trips > 0) && (
          <div
            data-testid="activity-leaderboard-pill"
            className="bg-gradient-to-br from-emerald-900/50 via-slate-900/60 to-slate-900/80 border border-emerald-800/60 rounded-2xl p-4 mb-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-600/40 flex items-center justify-center flex-shrink-0">
                <Trophy className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-100 leading-snug">
                  Alerted to <b className="text-emerald-400">{stats.alerted}</b> dry-out
                  {stats.alerted === 1 ? '' : 's'} · Saved <b className="text-emerald-400">{stats.saved}</b> time
                  {stats.saved === 1 ? '' : 's'}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  Last {stats.days} days · {stats.save_rate_pct}% action rate
                </div>
              </div>
            </div>

            {/* Water-saved receipt — the "neighbors don't notice, but I can prove it" line. */}
            {(stats.gallons_saved > 0 || stats.dollars_saved > 0) && (
              <div
                data-testid="water-saved-row"
                className="mt-3 pt-3 border-t border-slate-800/60 grid grid-cols-3 gap-2 text-center"
              >
                <div>
                  <div className="text-emerald-400 font-bold text-base leading-none">
                    {stats.gallons_saved.toLocaleString()}
                  </div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-1">gallons saved</div>
                </div>
                <div>
                  <div className="text-emerald-400 font-bold text-base leading-none">
                    ${stats.dollars_saved.toFixed(2)}
                  </div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-1">on your bill</div>
                </div>
                <div>
                  <div className="text-emerald-400 font-bold text-base leading-none">
                    {stats.minutes_saved}m
                  </div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-1">runtime skipped</div>
                </div>
              </div>
            )}

            {(stats.suppressed_high_temp > 0 || stats.auto_fired > 0 || stats.gate_trips > 0) && (
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-800/60 text-[11px] text-slate-400 flex-wrap">
                {stats.auto_fired > 0 && (
                  <span className="flex items-center gap-1">
                    <Droplets className="w-3 h-3 text-emerald-400" />
                    <b className="text-slate-200">{stats.auto_fired}</b> auto-fired
                  </span>
                )}
                {stats.gate_trips > 0 && (
                  <span className="flex items-center gap-1">
                    <Trophy className="w-3 h-3 text-emerald-400" />
                    <b className="text-slate-200">{stats.gate_trips}</b> smart skips
                  </span>
                )}
                {stats.suppressed_high_temp > 0 && (
                  <span className="flex items-center gap-1">
                    <Thermometer className="w-3 h-3 text-amber-400" />
                    <b className="text-slate-200">{stats.suppressed_high_temp}</b> held back (heat)
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Apply-history section — receipts for every "Apply All
            Recommendations" tap. Each row is proof the engine learned
            this yard and the user trusted it enough to apply. */}
        {applyHistory && applyHistory.count > 0 && (
          <div
            data-testid="apply-history-strip"
            className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 mb-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <Wand2 className="w-4 h-4 text-emerald-400" />
              <div className="text-sm font-semibold text-slate-100">
                Schedule tunes · last 90 days
              </div>
              <span className="ml-auto text-[10px] text-slate-500">
                {applyHistory.count} {applyHistory.count === 1 ? 'tune' : 'tunes'}
              </span>
            </div>
            <div className="space-y-2">
              {applyHistory.events.slice(0, 5).map((ev, i) => {
                const p = ev.payload || {};
                const when = (() => {
                  try {
                    return new Date(ev.fired_at).toLocaleString(undefined, {
                      month: 'short', day: 'numeric',
                      hour: 'numeric', minute: '2-digit',
                    });
                  } catch { return ev.fired_at; }
                })();
                const zones = (p.zones || []).slice(0, 3);
                return (
                  <div
                    key={i}
                    data-testid={`apply-history-row-${i}`}
                    className="bg-slate-950/60 border border-slate-800 rounded-lg p-2.5 text-xs"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-slate-200 font-semibold">
                        {p.applied_count} zone{p.applied_count === 1 ? '' : 's'} ·
                        {' '}{p.schedules_touched} schedule{p.schedules_touched === 1 ? '' : 's'}
                      </span>
                      <span className="text-[10px] text-slate-500">{when}</span>
                    </div>
                    <div className="text-[11px] text-slate-400 leading-snug">
                      {zones.map((z, j) => (
                        <span key={j} className="inline-block mr-2">
                          {z.name || z.rachio_zone_id}: {z.old_runtime_minutes}m → <b className="text-emerald-300">{z.new_runtime_minutes}m</b>
                        </span>
                      ))}
                      {(p.zones || []).length > 3 && (
                        <span className="text-slate-500">+{p.zones.length - 3} more</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {applyHistory.events.length > 5 && (
              <div className="text-[10px] text-slate-500 text-center mt-2">
                Showing 5 of {applyHistory.events.length} most recent tunes
              </div>
            )}
          </div>
        )}

        {err && (
          <div className="bg-rose-900/40 border border-rose-700/40 rounded-lg p-3 text-sm text-rose-300">
            Failed to load activity: {err}
          </div>
        )}
        {!data && !err && (
          <div className="text-sm text-slate-400 animate-pulse">Loading…</div>
        )}
        {data && data.events.length === 0 && (
          <div data-testid="activity-empty"
               className="text-center text-sm text-slate-400 py-10 bg-slate-900/60 border border-slate-800 rounded-2xl">
            No engine activity in the last {data.days} days yet.
            <div className="text-xs mt-2 text-slate-500">
              Once your schedule fires or you log a PGR app, it'll show up here.
            </div>
          </div>
        )}

        <div className="space-y-4" data-testid="activity-timeline">
          {data?.calibration_summary?.calibrated_zone_count > 0 && (
            <div
              data-testid="activity-calibration-banner"
              className="bg-gradient-to-r from-emerald-950/40 to-slate-900/60 border border-emerald-900/60 rounded-2xl p-3 flex items-center gap-3"
            >
              <div className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-600/40 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-100 leading-snug">
                  {data.calibration_summary.calibrated_zone_count} calibrated zone
                  {data.calibration_summary.calibrated_zone_count === 1 ? '' : 's'} driving decisions
                </div>
                <div className="text-[11px] text-slate-400">
                  Rows tagged "Calibrated" used your soil-specific FC/PWP/MAD targets.
                </div>
              </div>
              <button
                data-testid="activity-calibration-link"
                onClick={() => navigate('/sensors/calibration')}
                className="text-[11px] text-emerald-300 hover:text-emerald-200 underline whitespace-nowrap"
              >
                Manage
              </button>
            </div>
          )}
          {grouped.map(group => (
            <div key={group.day}>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 pl-1">
                {group.label}
              </div>
              <div className="space-y-2">
                {group.items.map((ev, i) => {
                  const s = STYLES[ev.kind] || STYLES.irrigation_fire;
                  const Icon = s.icon;
                  return (
                    <div key={i}
                         data-testid={`activity-row-${ev.kind}`}
                         className={`bg-slate-900/80 border ${s.ring} rounded-2xl px-3 py-2.5 flex items-start gap-3`}>
                      <div className={`w-9 h-9 rounded-full bg-slate-950 border border-slate-800 flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-4 h-4 ${s.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-100 flex items-center gap-2">
                          <span className="truncate">{ev.title}</span>
                          {ev.calibrated && (
                            <span
                              data-testid="activity-calibrated-badge"
                              title="A calibrated zone (Soil Calibration Wizard) participated in this event"
                              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-emerald-300 bg-emerald-950/60 border border-emerald-800/60 rounded-full px-1.5 py-0.5 flex-shrink-0"
                            >
                              <Sparkles className="w-2.5 h-2.5" />
                              Calibrated
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 leading-snug truncate">{ev.subtitle}</div>
                      </div>
                      <div className="text-[10px] text-slate-500 flex-shrink-0">{relTime(ev.ts)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-slate-950/95 backdrop-blur border-t border-slate-800 z-30">
        <div className="max-w-[calc(100%-185px)] px-3 py-1.5 grid grid-cols-5 text-center text-[10px] text-slate-400 gap-1">
          <button data-testid="nav-home"     onClick={() => navigate('/pulse')}  className="flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><Home className="w-5 h-5"/><span>Home</span></button>
          <button data-testid="nav-history"  className="flex flex-col items-center gap-1 py-1.5 text-emerald-400"><Clock className="w-5 h-5"/><span>History</span></button>
          <button data-testid="nav-trends"   onClick={() => navigate('/trends')} className="flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><BarChart3 className="w-5 h-5"/><span>Trends</span></button>
          <button data-testid="nav-alerts"   onClick={() => navigate('/alerts')} className="relative flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><span className="relative"><Bell className="w-5 h-5"/><AlertDot total={unreadTotal} high={unreadHigh} /></span><span>Alerts</span></button>
          <button data-testid="nav-settings" onClick={() => navigate('/profile')} className="flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><SettingsIcon className="w-5 h-5"/><span>Settings</span></button>
        </div>
      </div>
    </div>
  );
}
